import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Alert,
    CircularProgress,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import PageHeader from '../components/shared/page-header';
import { useContactController } from './context/contact-controller';
import { useContactState } from './context/contact-context';

interface FormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

const initialFormData: FormData = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
};

export function ContactComponent() {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [captchaToken, setCaptchaToken] = useState<string>('');
    const [formErrors, setFormErrors] = useState<Partial<FormData>>({});

    const state = useContactState();
    const controller = useContactController();

    // Reset form and captcha after success or error
    useEffect(() => {
        if (state.success) {
            setFormData(initialFormData);
            setCaptchaToken('');
            setFormErrors({});
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
        }
    }, [state.success]);

    useEffect(() => {
        if (state.error) {
            setCaptchaToken('');
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
        }
    }, [state.error]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error for this field when user starts typing
        if (formErrors[name as keyof FormData]) {
            setFormErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    const handleCaptchaChange = (token: string | null) => {
        setCaptchaToken(token || '');
    };

    const validateForm = (): boolean => {
        const errors: Partial<FormData> = {};

        if (!formData.name.trim()) {
            errors.name = 'Nome é obrigatório';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email é obrigatório';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Email inválido';
        }

        if (!formData.subject.trim()) {
            errors.subject = 'Assunto é obrigatório';
        }

        if (!formData.message.trim()) {
            errors.message = 'Mensagem é obrigatória';
        } else if (formData.message.length > 1000) {
            errors.message = 'Mensagem deve ter no máximo 1000 caracteres';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!captchaToken) {
            return;
        }

        await controller.sendMessage({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            subject: formData.subject,
            message: formData.message,
            recaptchaToken: captchaToken,
        });
    };

    const isSubmitDisabled = state.loading || !captchaToken;

    return (
        <Box sx={{ py: 4 }}>
            <PageHeader
                title="Fale Conosco"
                subtitle="Entre em contato conosco. Responderemos em breve."
                breadcrumbs={[{ label: 'Contato' }]}
            />

            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
                {state.success && (
                    <Alert
                        severity="success"
                        sx={{ mb: 3 }}
                        onClose={() => controller.resetState()}
                    >
                        Mensagem enviada com sucesso! Entraremos em contato em breve.
                    </Alert>
                )}

                {state.error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        onClose={() => controller.resetState()}
                    >
                        {state.error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Nome"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                disabled={state.loading}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                disabled={state.loading}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Telefone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                error={!!formErrors.phone}
                                helperText={formErrors.phone}
                                disabled={state.loading}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Assunto"
                                name="subject"
                                value={formData.subject}
                                onChange={handleInputChange}
                                error={!!formErrors.subject}
                                helperText={formErrors.subject}
                                disabled={state.loading}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Mensagem"
                                name="message"
                                multiline
                                rows={6}
                                value={formData.message}
                                onChange={handleInputChange}
                                error={!!formErrors.message}
                                helperText={formErrors.message || `${formData.message.length}/1000 caracteres`}
                                disabled={state.loading}
                                inputProps={{ maxLength: 1000 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey="6LdrVRcsAAAAACmjuhv9CsQfjA8lWNpwAVQbrqv3"
                                    onChange={handleCaptchaChange}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={isSubmitDisabled}
                                startIcon={state.loading ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {state.loading ? 'Enviando...' : 'Enviar Mensagem'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
}
